import type { ReactNode } from 'react';

/**
 * Renders bridge call tokens found inside authored prose as proper bridge notation, leaving every
 * other character exactly as written (feature 029, FR-022).
 *
 * <p><b>Why this exists.</b> The constitution's Suit Symbol Display standard requires suit letters
 * to become suit symbols "when parsing bid/card notation from JSON or any text source", and the
 * conceptual problems' questions and answers are full of such notation — 595 questions and 103
 * answers carry a call token. "What is the meaning of partners 3h?" should read 3♥, the way it
 * would anywhere else on the site.</p>
 *
 * <p><b>Why it converts so little.</b> Only call-SHAPED tokens are touched: a level 1-7 followed
 * by a suit letter or NT, and the dbl/rdbl abbreviations. Suit WORDS are deliberately
 * left alone, and that is the whole reason this is a narrow matcher rather than a general one —
 * "5 spades by responder", "no heart stopper" and "you pitch on partners 3rd cashing diamond" are
 * the editors' English, not notation, and rewriting them would corrupt the very prose an editor
 * has come to this page to read.</p>
 *
 * <p>Word boundaries do the work: `3h` in "partners 3h?" converts, `3rd` does not, and "2 hearts"
 * does not. Returns an array of nodes suitable for rendering directly as children.</p>
 */

/** A level 1-7 followed by a denomination. Word-bounded so `3rd` and `1st` are never matched. */
const CALL_TOKEN = /\b([1-7])(NT|C|D|H|S)\b/gi;

/**
 * The double/redouble ABBREVIATIONS, in the spellings the data uses (dbl, Dbl, DBL, rdbl, ...).
 * 135 problems carry dbl and 25 carry rdbl; those are notation, and become X and XX.
 *
 * The spelled-out words are deliberately NOT here. All 35 uses of the word "double" in this data
 * set are ordinary bridge English - "a takeout double", "the support double", "a card showing
 * double", "what is the meaning of partner's double?", and, in the longest answer of the whole
 * set, "helping partner evaluate double fits". Every one of them rendered as "X" while the word
 * was matched, which is exactly the corruption FR-022's second half forbids: everything that is
 * not notation must read as the editor wrote it. FR-022 names the dbl/rdbl forms, and those only.
 */
const DOUBLE_TOKEN = /\b(rdbl|dbl)\b/gi;

/** Combined, so one pass over the string handles both. Order matters: longest alternatives first. */
const ANY_TOKEN = new RegExp(`(${DOUBLE_TOKEN.source})|(${CALL_TOKEN.source})`, 'gi');

const SUIT_SYMBOLS: Record<string, { glyph: string; red: boolean }> = {
  C: { glyph: '♣', red: false },
  D: { glyph: '♦', red: true },
  H: { glyph: '♥', red: true },
  S: { glyph: '♠', red: false },
};

/**
 * Splits `text` into plain strings and rendered call tokens.
 *
 * @returns nodes to render as children. An empty array for empty/null input.
 */
export function renderCallTokens(text: string | null | undefined): ReactNode[] {
  if (!text) return [];

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  // `matchAll` needs the global flag, and a fresh regex each call keeps lastIndex from leaking
  // between invocations — a shared global regex is stateful and would drop matches on every
  // second call.
  const pattern = new RegExp(ANY_TOKEN.source, 'gi');

  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;

    if (index > lastIndex) {
      nodes.push(text.slice(lastIndex, index));
    }

    const token = match[0];
    nodes.push(<CallToken key={`call-${key++}`} token={token} />);
    lastIndex = index + token.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

/**
 * One rendered call. Uses the same markup as the auction's own bid cells — `font-suits` (Arimo,
 * with Arial behind it) and red diamonds/hearts — so a call reads identically whether it appears
 * in the auction or in a sentence about the auction.
 */
function CallToken({ token }: { token: string }) {
  const doubleMatch = token.match(/^(rdbl|dbl)$/i);
  if (doubleMatch) {
    const isRedouble = /^r/i.test(token);
    return <span className="font-semibold text-ink">{isRedouble ? 'XX' : 'X'}</span>;
  }

  const level = token[0];
  const denomination = token.slice(1).toUpperCase();

  if (denomination === 'NT') {
    return (
      <span className="whitespace-nowrap font-semibold text-ink">
        {level}
        <span style={{ marginLeft: '0.08em' }}>NT</span>
      </span>
    );
  }

  const suit = SUIT_SYMBOLS[denomination];
  if (!suit) {
    // Unreachable given the matcher, but a token that cannot be classified is shown as written
    // rather than dropped — the same rule the auction display follows.
    return <span>{token}</span>;
  }

  return (
    <span className="whitespace-nowrap font-semibold text-ink">
      {level}
      <span
        className={`font-suits ${suit.red ? 'text-red-600' : 'text-black'}`}
        style={{ marginLeft: '0.08em' }}
        aria-hidden="true"
      >
        {suit.glyph}
      </span>
      <span className="sr-only">{` ${denomination === 'C' ? 'clubs' : denomination === 'D' ? 'diamonds' : denomination === 'H' ? 'hearts' : 'spades'}`}</span>
    </span>
  );
}
